/* ==========================================================================
   OliveDrab — motion engine
   No dependencies. Loaded with defer.

   Safety contract: content is only ever hidden after this script has confirmed
   it can reveal it again. If IntersectionObserver is missing, if motion is
   turned off in the theme settings, or if anything below throws, the page
   renders fully visible and static.

   Designed by Thriftizer.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var cfg = window.ODConfig || {};

  /* ---------------------------------------------------------------- utils */

  function on(el, ev, fn, opts) { el.addEventListener(ev, fn, opts || false); }
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  var reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function prefersReduced() {
    return !!(cfg.respectReduced && reducedQuery && reducedQuery.matches);
  }

  // rAF throttle for scroll driven work
  function rafThrottle(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  function supports() {
    return 'IntersectionObserver' in window &&
           'requestAnimationFrame' in window &&
           typeof document.documentElement.classList !== 'undefined';
  }

  var MOTION = !!cfg.motion && supports() && !prefersReduced();

  /* ------------------------------------------------------------- boot flags */

  root.classList.remove('no-js');
  if (cfg.respectReduced) root.classList.add('od-respect-reduced');
  if (cfg.brackets) document.body.classList.add('od-brackets');
  root.setAttribute('data-od-hover', cfg.hoverZoom || 'none');
  root.setAttribute('data-od-swap', cfg.hoverSwap ? 'true' : 'false');
  if (cfg.smoothAnchor) root.classList.add('od-smooth-scroll');

  /* --------------------------------------------------------------- 1. reveal */

  function initReveal() {
    var style = cfg.revealStyle || 'rise';
    if (!MOTION || style === 'none') return;

    root.setAttribute('data-od-reveal', style);
    // only now is it safe to let CSS hide things
    root.classList.add('od-can-reveal');

    var stagger = parseInt(cfg.stagger, 10) || 0;
    var threshold = (parseInt(cfg.threshold, 10) || 0) / 100;
    var margin = '0px 0px -' + Math.round(threshold * 100) + '% 0px';

    var io = new IntersectionObserver(function (entries) {
      // group entries that land together so their stagger reads as one wave
      var batch = [];
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) batch.push(entries[i].target);
      }
      batch.sort(function (a, b) {
        var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
        return (ra.top - rb.top) || (ra.left - rb.left);
      });
      batch.forEach(function (el, i) {
        var group = el.getAttribute('data-od-r');
        var index = group === 'item' ? i : 0;
        if (stagger && index) el.style.setProperty('--od-d', (index * stagger) + 'ms');
        el.classList.add('od-in');
        io.unobserve(el);
      });
    }, { rootMargin: margin, threshold: 0.01 });

    $$('[data-od-r]').forEach(function (el) {
      // anything already in view on load reveals immediately, no wait
      io.observe(el);
    });

    // sections added later by the theme editor
    document.addEventListener('shopify:section:load', function (e) {
      $$('[data-od-r]', e.target).forEach(function (el) { io.observe(el); });
      initSplit(e.target);
      initMarquee(e.target);
      initMedia(e.target);
    });
  }

  /* ---------------------------------------------------- 2. split headings */

  function initSplit(ctx) {
    if (!MOTION || !cfg.splitLines) return;

    $$('[data-od-split]', ctx).forEach(function (el) {
      if (el.dataset.odSplitDone) return;

      // Wrap each rendered line in its own overflow box. Measured from the
      // live layout so it survives any font or width.
      var text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return;

      var words = text.split(' ');
      el.textContent = '';
      var probes = words.map(function (w, i) {
        var s = document.createElement('span');
        s.textContent = w + (i < words.length - 1 ? ' ' : '');
        s.style.display = 'inline';
        el.appendChild(s);
        return s;
      });

      var lines = [];
      var currentTop = null;
      probes.forEach(function (s) {
        var top = s.offsetTop;
        if (currentTop === null || Math.abs(top - currentTop) > 2) {
          lines.push([]);
          currentTop = top;
        }
        lines[lines.length - 1].push(s.textContent);
      });

      el.textContent = '';
      el.classList.add('od-split');
      var stagger = parseInt(cfg.stagger, 10) || 0;
      lines.forEach(function (words, i) {
        var line = document.createElement('span');
        line.className = 'od-split__line';
        var inner = document.createElement('span');
        inner.className = 'od-split__inner';
        inner.textContent = words.join('');
        if (stagger) inner.style.setProperty('--od-d', (i * Math.min(stagger, 120)) + 'ms');
        line.appendChild(inner);
        el.appendChild(line);
      });

      el.dataset.odSplitDone = '1';

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('od-in');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      io.observe(el);
    });
  }

  /* --------------------------------------------------------- 3. marquee */

  function initMarquee(ctx) {
    $$('[data-od-marquee]', ctx).forEach(function (el) {
      var track = $('.od-marquee__track', el);
      if (!track || track.dataset.odCloned) return;

      // The animation stylesheet loads without blocking the page, so it may
      // not have arrived yet. Measured without it, the items wrap onto new
      // lines, the track never gets wider, and the copying below used to run
      // to its limit and flood the screen with the bar. Measure the track as a
      // single line whatever has loaded.
      el.style.display = 'flex';
      el.style.overflow = 'hidden';
      track.style.display = 'flex';
      track.style.flex = 'none';
      track.style.whiteSpace = 'nowrap';

      // duplicate content until it comfortably exceeds the viewport, so the
      // -100% keyframe is always a seamless loop
      var original = track.innerHTML;
      var one = track.scrollWidth;
      var want = el.offsetWidth * 2;
      if (!one || !want) return; // not laid out yet (hidden); tried again on load
      var copies = Math.min(12, Math.max(0, Math.ceil(want / one) - 1));
      for (var n = 0; n < copies; n++) track.innerHTML += original;
      track.dataset.odCloned = '1';

      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      // the repeat is decoration: nothing in it can take keyboard focus
      clone.setAttribute('inert', '');
      $$('a, button', clone).forEach(function (c) { c.setAttribute('tabindex', '-1'); });
      el.appendChild(clone);
    });
  }

  /* -------------------------------------------------------- 4. parallax */

  function initParallax() {
    var strength = parseFloat(cfg.parallax) || 0;
    if (!MOTION || strength <= 0) return;

    var items = $$('[data-od-parallax]');
    if (!items.length) return;

    var active = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = active.indexOf(entry.target);
        if (entry.isIntersecting && i === -1) active.push(entry.target);
        else if (!entry.isIntersecting && i > -1) active.splice(i, 1);
      });
    }, { rootMargin: '20% 0px 20% 0px' });

    items.forEach(function (el) { io.observe(el); });

    var update = rafThrottle(function () {
      var vh = window.innerHeight;
      for (var i = 0; i < active.length; i++) {
        var el = active[i];
        var rect = el.getBoundingClientRect();
        var per = parseFloat(el.getAttribute('data-od-parallax')) || 1;
        // -1 .. 1 across the viewport
        var progress = ((rect.top + rect.height / 2) - vh / 2) / vh;
        var shift = -progress * strength * 100 * per;
        el.style.setProperty('--od-py', shift.toFixed(2) + 'px');
      }
    });

    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', update, { passive: true });
    update();
  }

  /* ------------------------------------------------- 5. media load state */

  function initMedia(ctx) {
    $$('.od-media', ctx).forEach(function (wrap) {
      if (wrap.dataset.odMediaBound) return;
      wrap.dataset.odMediaBound = '1';
      var img = wrap.querySelector('img:not(.od-media__lqip)');
      if (!img) return;
      if (img.complete && img.naturalWidth) {
        wrap.classList.add('is-loaded');
      } else {
        on(img, 'load', function () { wrap.classList.add('is-loaded'); });
        on(img, 'error', function () { wrap.classList.add('is-loaded'); });
      }
    });
  }

  /* ----------------------------------------------------- 6. count up */

  function initCount() {
    var items = $$('[data-od-count]');
    if (!items.length) return;

    if (!MOTION) {
      items.forEach(function (el) { el.textContent = el.getAttribute('data-od-count'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        var target = parseFloat(el.getAttribute('data-od-count')) || 0;
        var dur = 1100;
        var start = null;
        var suffix = el.getAttribute('data-od-count-suffix') || '';
        (function step(ts) {
          if (start === null) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased) + suffix;
          if (p < 1) requestAnimationFrame(step);
        })(performance.now());
      });
    }, { rootMargin: '0px 0px -10% 0px' });

    items.forEach(function (el) { io.observe(el); });
  }

  /* -------------------------------------------------- 7. video on demand */

  function initVideo() {
    var vids = $$('video[data-od-lazy]');
    if (!vids.length) return;

    var mobile = window.matchMedia('(max-width: 749px)').matches;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          if (mobile && cfg.videoMobile === false) return;
          if (!v.dataset.odLoaded) {
            $$('source[data-src]', v).forEach(function (s) {
              s.src = s.getAttribute('data-src');
            });
            v.load();
            v.dataset.odLoaded = '1';
          }
          var p = v.play();
          if (p && p.catch) p.catch(function () { /* autoplay blocked, fine */ });
        } else if (cfg.videoPause !== false) {
          v.pause();
        }
      });
    }, { rootMargin: '100px 0px', threshold: 0.2 });

    vids.forEach(function (v) { io.observe(v); });
  }

  /* ------------------------------------------------- 8. hover prefetch */

  function initPrefetch() {
    if (!cfg.prefetch) return;
    if (navigator.connection && (navigator.connection.saveData ||
        /2g/.test(navigator.connection.effectiveType || ''))) return;

    var done = {};
    function warm(href) {
      if (!href || done[href]) return;
      done[href] = 1;
      var l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = href;
      l.as = 'document';
      document.head.appendChild(l);
    }

    on(document, 'mouseover', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      var url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      if (/\/(cart|checkout|account)(\/|$)/.test(url.pathname)) return;
      if (url.pathname === location.pathname) return;
      warm(url.pathname + url.search);
    }, { passive: true });
  }

  /* ------------------------------------------------------- 9. anchors */

  function initAnchors() {
    if (!cfg.smoothAnchor) return;
    on(document, 'click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (!id || id === '#' || id.length < 2) return;
      var target;
      try { target = document.querySelector(id); } catch (err) { return; }
      if (!target) return;
      e.preventDefault();
      var top = target.getBoundingClientRect().top + window.pageYOffset -
                (parseInt(cfg.headerHeight, 10) || 0) - 16;
      window.scrollTo({
        top: top,
        behavior: prefersReduced() ? 'auto' : 'smooth'
      });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  /* -------------------------------------------------------- 10. toast */

  var toastEl = null;
  var toastTimer = null;

  function toast(message) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'od-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    // force a frame so the transition runs on repeat calls
    requestAnimationFrame(function () { toastEl.classList.add('is-open'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-open');
    }, 2600);
  }

  /* ----------------------------------------------------------- public */

  window.OD = window.OD || {};
  window.OD.toast = toast;
  window.OD.$ = $;
  window.OD.$$ = $$;
  window.OD.on = on;
  window.OD.motion = MOTION;
  window.OD.prefersReduced = prefersReduced;
  window.OD.rafThrottle = rafThrottle;
  window.OD.refresh = function (ctx) {
    try {
      initSplit(ctx || document);
      initMarquee(ctx || document);
      initMedia(ctx || document);
    } catch (e) { /* never block the page */ }
  };

  /* ------------------------------------------------------------- start */

  function start() {
    var steps = [initReveal, initSplit, initMarquee, initParallax,
                 initMedia, initCount, initVideo, initPrefetch, initAnchors];
    for (var i = 0; i < steps.length; i++) {
      try { steps[i](document); } catch (e) {
        if (window.console && console.warn) console.warn('[OD motion]', e);
      }
    }
    root.classList.add('od-ready');
  }

  if (document.readyState === 'loading') {
    on(document, 'DOMContentLoaded', start);
  } else {
    start();
  }

  // last line of defence: if anything left content hidden, show it
  on(window, 'load', function () {
    // a marquee that could not be measured earlier gets its copies now
    try { initMarquee(document); } catch (e) { /* never block the page */ }
    setTimeout(function () {
      $$('[data-od-r]:not(.od-in)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('od-in');
      });
    }, 400);
  });
})();
